import {
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Button,
} from '@mui/material';
import React, { createContext, useContext, useState, useCallback } from 'react';

const SnackbarContext = createContext();

let snackbarRef = null;
export function setGlobalSnackbar(fn) {
  snackbarRef = fn;
}
export function globalSnackbar(args) {
  if (snackbarRef) snackbarRef(args);
  else if (typeof window !== 'undefined') {
    // fallback: alert for dev
    alert(args.message);
  }
}

const ConfirmContext = createContext();
let confirmRef = null;
export function setGlobalConfirm(fn) {
  confirmRef = fn;
}
export function globalConfirm({
  message,
  onConfirm,
  onCancel,
  confirmText = 'OK',
  cancelText = 'Отмена',
  title = 'Подтверждение',
}) {
  if (confirmRef) confirmRef({ message, onConfirm, onCancel, confirmText, cancelText, title });
  else if (typeof window !== 'undefined') {
    if (window.confirm(message)) onConfirm && onConfirm();
    else onCancel && onCancel();
  }
}
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a GlobalConfirmProvider');
  return ctx.showConfirm;
}
export function GlobalConfirmProvider({ children }) {
  const [state, setState] = React.useState({ open: false });
  const handleClose = () => {
    setState({ open: false });
    state.onCancel && state.onCancel();
  };
  const handleConfirm = () => {
    setState({ open: false });
    state.onConfirm && state.onConfirm();
  };
  const showConfirm = React.useCallback((opts) => setState({ ...opts, open: true }), []);
  React.useEffect(() => {
    setGlobalConfirm(showConfirm);
    return () => setGlobalConfirm(null);
  }, [showConfirm]);
  return (
    <ConfirmContext.Provider value={{ showConfirm }}>
      {children}
      <Dialog open={!!state.open} onClose={handleClose}>
        <DialogTitle>{state.title || 'Подтверждение'}</DialogTitle>
        <DialogContent>
          <Typography>{state.message}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
            <Button onClick={handleClose}>{state.cancelText || 'Отмена'}</Button>
            <Button color="error" variant="contained" onClick={handleConfirm}>
              {state.confirmText || 'OK'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function GlobalSnackbarProvider({ children }) {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const showSnackbar = useCallback(
    ({ message, severity = 'info', duration = 3000, autoClose = true }) => {
      setSnackbar({ open: true, message, severity, duration, autoClose });
    },
    []
  );

  const handleClose = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  React.useEffect(() => {
    setGlobalSnackbar(showSnackbar);
    return () => setGlobalSnackbar(null);
  }, [showSnackbar]);

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.autoClose ? snackbar.duration || 3000 : null}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleClose}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            whiteSpace: 'pre-line',
            '& .MuiAlert-message': {
              width: '100%',
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
}

export function useSnackbar() {
  const ctx = useContext(SnackbarContext);
  if (!ctx) throw new Error('useSnackbar must be used within a GlobalSnackbarProvider');
  return ctx.showSnackbar;
}
